export type TvAudioEvent={id:string;text:string;automatic:boolean;expires_at:string};
export type TvAudioPolicy={enabled:boolean;volume:number;duckVideo:boolean;version:number};
export type TvAudioFeed={policy:TvAudioPolicy;serverTime:string;events:TvAudioEvent[]};
export type TvAudioStatus='READY'|'PLAYING'|'BLOCKED'|'ERROR';
type AudioPort=Pick<HTMLAudioElement,'src'|'volume'|'play'|'pause'|'onended'|'onerror'>;
type Ports={
 audio:AudioPort;now:()=>number;file:(id:string,signal:AbortSignal)=>Promise<Blob>;
 start:(id:string,signal:AbortSignal)=>Promise<{permitted:boolean}>;
 finish:(id:string,outcome:'ENDED'|'FAILED')=>Promise<unknown>;
 url:(blob:Blob)=>string;revoke:(url:string)=>void;
 change:(status:TvAudioStatus,text:string,duck:boolean)=>void;
};
// One audio element and one reservation per screen/event. Never retry a sound
// automatically after it may have started; a receipt is not proof of hearing.
export class TvAudioController{
 status:TvAudioStatus='READY';detail='';since:string|undefined;
 private policy:TvAudioPolicy|undefined;private offset=0;private queue:TvAudioEvent[]=[];
 private current:TvAudioEvent|undefined;private objectUrl='';private reserved=false;
 private playingAt:number|undefined;
 private epoch=0;private fetching=false;private abort=new AbortController();
 constructor(private p:Ports){p.audio.onended=()=>this.complete('ENDED');p.audio.onerror=()=>this.complete('FAILED')}
 private expired(e:TvAudioEvent){return Date.parse(e.expires_at)<=this.p.now()+this.offset}
 private update(status:TvAudioStatus,text=''){this.status=status;this.detail=text;this.p.change(status,text,status==='PLAYING'&&!!this.policy?.duckVideo)}
 accept(feed:TvAudioFeed){
  this.offset=Date.parse(feed.serverTime)-this.p.now();
  if(!this.since){this.since=feed.serverTime;this.policy=feed.policy;return}
  if(this.policy?.version!==feed.policy.version)this.clear();
  this.policy=feed.policy;this.p.audio.volume=feed.policy.volume/100;
  if(this.current&&(this.expired(this.current)||this.current.automatic&&!feed.policy.enabled||this.playingAt!==undefined&&this.p.now()-this.playingAt>30000))this.complete('FAILED');
  const ids=new Set([...this.queue.map(e=>e.id),...(this.current?[this.current.id]:[])]);
  for(const e of feed.events)if(!ids.has(e.id)&&!this.expired(e)&&(!e.automatic||feed.policy.enabled)){this.queue.push(e);ids.add(e.id)}
  this.queue=this.queue.filter(e=>!this.expired(e)&&(!e.automatic||feed.policy.enabled)).slice(0,20);
  if(this.status==='ERROR'&&!this.current)this.update('READY');
  void this.next();
 }
 private async next(){
  if(this.current||this.fetching||!this.policy)return;
  const e=this.queue.shift();if(!e)return;
  this.current=e;this.fetching=true;const epoch=this.epoch;
  try{
   const blob=await this.p.file(e.id,this.abort.signal);if(epoch!==this.epoch)return;
   if(this.expired(e)){this.complete('FAILED');return}
   const claim=await this.p.start(e.id,this.abort.signal);if(epoch!==this.epoch)return;
   if(!claim.permitted){this.current=undefined;return}
   this.reserved=true;this.objectUrl=this.p.url(blob);this.p.audio.src=this.objectUrl;
   this.p.audio.volume=this.policy.volume/100;
   if(this.expired(e)){this.complete('FAILED');return}
   await this.play(epoch);
  }catch(err){if(epoch===this.epoch){this.complete('FAILED');this.update('ERROR',(err as Error).message)}}
  finally{if(epoch===this.epoch){this.fetching=false;if(!this.current)void this.next()}}
 }
 private async play(epoch:number){
  try{await this.p.audio.play();if(epoch===this.epoch&&this.current){this.playingAt=this.p.now();this.update('PLAYING',this.current.text)}}
  catch(err){if(epoch!==this.epoch)return;if((err as Error).name==='NotAllowedError')this.update('BLOCKED','Nhấn OK để bật âm thanh TV');else{this.complete('FAILED');this.update('ERROR','Không phát được âm thanh TV')}}
 }
 // Called synchronously inside the trusted remote key/click handler.
 unlock(){if(this.status!=='BLOCKED'||!this.current)return;if(this.expired(this.current)){this.complete('FAILED');return}void this.play(this.epoch)}
 private complete(outcome:'ENDED'|'FAILED'){
  const e=this.current;if(!e)return;
  this.p.audio.pause();if(this.objectUrl)this.p.revoke(this.objectUrl);
  this.objectUrl='';this.current=undefined;this.playingAt=undefined;
  if(this.reserved)void this.p.finish(e.id,outcome).catch(()=>{});
  this.reserved=false;this.update('READY');if(!this.fetching)void this.next();
 }
 networkError(message:string){this.reset();this.update('ERROR',message)}
 private clear(){this.epoch++;this.abort.abort();this.abort=new AbortController();this.queue=[];this.fetching=false;this.complete('FAILED');this.update('READY')}
 reset(){this.clear();this.since=undefined;this.policy=undefined}
 dispose(){this.reset();this.p.audio.onended=null;this.p.audio.onerror=null}
}
